/**
 * Events frontend filter config and normalization (FRONTEND-ONLY).
 * Derives attendanceMode, curatedSeriesLabel, regionLabel, isLocalGathering from API event shape.
 * No backend changes.
 */

/** Attendance type filter options. */
export const ATTENDANCE_OPTIONS = [
  { id: "virtual", label: "Virtual" },
  { id: "in-person", label: "In Person" },
];

/** Curated series filter options. "All Events" = no selection. */
export const CURATED_SERIES_OPTIONS = [
  { id: "moving-together", label: "Moving Together" },
  { id: "mama-hood-experience", label: "Mama Hood Experience" },
];

/** Location filter options. MVP: Bay Area, Virtual. TODO: expand regions later. */
export const LOCATION_OPTIONS = [
  { id: "bay-area", label: "Bay Area" },
  { id: "virtual", label: "Virtual" },
];

function norm(s) {
  if (typeof s !== "string") return "";
  return s.toLowerCase().trim();
}

/**
 * Normalize a raw API event for filtering. Adds attendanceMode, curatedSeriesLabel, regionLabel, isLocalGathering.
 * @param {Object} ev - Event from API (id, name, location, short_description, event_type, etc.)
 * @returns {Object} Event with normalized filter fields
 */
export function normalizeEvent(ev) {
  if (!ev || typeof ev !== "object") return ev;
  const location = norm(ev.location || "");
  const name = norm(ev.name || "");
  const desc = norm(ev.short_description || "");
  const eventType = norm(ev.event_type || ev.category || ev.series || "");

  let attendanceMode = "in-person";
  if (ev.attendance_mode) {
    const m = norm(String(ev.attendance_mode));
    if (m === "virtual" || m === "online") attendanceMode = "virtual";
    else if (m === "inperson" || m === "in-person" || m === "offline") attendanceMode = "in-person";
  } else if (location.includes("virtual") || eventType.includes("virtual") || name.includes("virtual") || desc.includes("virtual")) {
    attendanceMode = "virtual";
  }

  let curatedSeriesLabel = null;
  if (ev.curated_series) {
    const s = norm(String(ev.curated_series));
    if (s.includes("moving") && s.includes("together")) curatedSeriesLabel = "moving-together";
    else if (s.includes("mama") && (s.includes("hood") || s.includes("experience"))) curatedSeriesLabel = "mama-hood-experience";
  }
  if (!curatedSeriesLabel && eventType) {
    if (eventType.includes("moving") && eventType.includes("together")) curatedSeriesLabel = "moving-together";
    else if (eventType.includes("mama") || eventType.includes("hood") || eventType.includes("experience")) curatedSeriesLabel = "mama-hood-experience";
  }
  if (!curatedSeriesLabel && (name.includes("moving together") || desc.includes("moving together"))) curatedSeriesLabel = "moving-together";
  if (!curatedSeriesLabel && (name.includes("mama hood") || desc.includes("mama hood experience"))) curatedSeriesLabel = "mama-hood-experience";

  let regionLabel = "bay-area";
  if (attendanceMode === "virtual") regionLabel = "virtual";
  else if (ev.region) {
    const r = norm(String(ev.region));
    if (r.includes("virtual")) regionLabel = "virtual";
    else if (r.includes("bay") || r.includes("sf") || r.includes("oakland") || r.includes("san francisco")) regionLabel = "bay-area";
  } else if (location) {
    if (location.includes("virtual") || location.includes("online")) regionLabel = "virtual";
    else if (location.includes("bay") || location.includes("sf") || location.includes("oakland") || location.includes("san francisco") || location.includes("berkeley")) regionLabel = "bay-area";
  }

  const isLocalGathering = attendanceMode === "in-person" && regionLabel === "bay-area";

  return {
    ...ev,
    attendanceMode,
    curatedSeriesLabel,
    regionLabel,
    isLocalGathering,
  };
}

/**
 * Apply frontend-only filters to a list of normalized events.
 * @param {Object[]} events - Normalized events (with attendanceMode, curatedSeriesLabel, regionLabel)
 * @param {Object} filterState - { search, attendanceIds, curatedSeriesIds, locationIds }
 */
export function applyEventsFilters(events, filterState) {
  if (!Array.isArray(events)) return [];
  let out = events;

  const {
    search = "",
    attendanceIds = [],
    curatedSeriesIds = [],
    locationIds = [],
  } = filterState;

  const searchNorm = search.trim().toLowerCase();
  if (searchNorm) {
    out = out.filter((e) => {
      const name = (e.name || "").toLowerCase();
      const loc = (e.location || "").toLowerCase();
      const desc = (e.short_description || "").toLowerCase();
      return name.includes(searchNorm) || loc.includes(searchNorm) || desc.includes(searchNorm);
    });
  }

  if (attendanceIds.length > 0) {
    out = out.filter((e) => attendanceIds.includes(e.attendanceMode));
  }

  if (curatedSeriesIds.length > 0) {
    out = out.filter((e) => e.curatedSeriesLabel && curatedSeriesIds.includes(e.curatedSeriesLabel));
  }

  if (locationIds.length > 0) {
    out = out.filter((e) => locationIds.includes(e.regionLabel));
  }

  return out;
}
